import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface PatternAlert {
  id: string
  type: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  count: number
  status: 'increasing' | 'decreasing' | 'stable'
  description: string
}

interface ActivePatternsCardProps {
  patterns: PatternAlert[]
}

export const ActivePatternsCard: React.FC<ActivePatternsCardProps> = ({ patterns }) => {
  const defaultPatterns: PatternAlert[] = [
    {
      id: '1',
      type: 'Phone Sequences',
      severity: 'critical',
      count: 12,
      status: 'increasing',
      description: '12 active sequences'
    },
    {
      id: '2', 
      type: 'Data Center IPs',
      severity: 'high',
      count: 34,
      status: 'stable',
      description: '34 blocked IPs'
    },
    {
      id: '3',
      type: 'Velocity Attacks', 
      severity: 'medium',
      count: 7,
      status: 'decreasing',
      description: '7 today'
    },
    {
      id: '4',
      type: 'Suspicious Domains',
      severity: 'medium', 
      count: 23,
      status: 'stable',
      description: '23 flagged domains'
    }
  ]

  const displayPatterns = patterns.length > 0 ? patterns : defaultPatterns

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 dark:text-red-400'
      case 'high': return 'text-orange-600 dark:text-orange-400'
      case 'medium': return 'text-yellow-600 dark:text-yellow-400'
      default: return 'text-blue-600 dark:text-blue-400'
    }
  }

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive'
      case 'high': return 'default'
      case 'medium': return 'secondary'
      default: return 'outline'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'increasing': return <TrendingUp className="h-3 w-3 text-red-500" />
      case 'decreasing': return <TrendingDown className="h-3 w-3 text-green-500" />
      default: return <Minus className="h-3 w-3 text-gray-500" />
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className="h-5 w-5" />
          Active Patterns
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {displayPatterns.map((pattern) => (
            <div key={pattern.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-foreground">{pattern.type}</span>
                  <Badge variant={getSeverityBadge(pattern.severity) as any} className="text-xs">
                    {pattern.severity}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{pattern.description}</p>
              </div>
              
              <div className="flex items-center gap-3 ml-4">
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getSeverityColor(pattern.severity)}`}>
                    {pattern.count}
                  </p>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(pattern.status)}
                    <span className="text-xs text-muted-foreground capitalize">
                      {pattern.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {displayPatterns.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No active patterns detected</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}